import { useEffect, useMemo, useState } from "react";
import { loadTemplates } from "../../api/excelLayoutTemplates";
import { defaultColumnAxisOptions, makeDefaultExcelMeasure, makeDefaultExcelRowLabel as defaultRowLabelOptions } from "../../logic/excelLayoutDefaults";
import { reportToast } from "../../../../../components/notifications/appToast";
import type {
    DateGranularity,
    DisplayAs,
    ExcelCellFormat,
    ExcelChartDataOrientation,
    ExcelChartLayout,
    ExcelChartMode,
    ExcelChartType,
    ExcelColumnLayout,
    ExcelColumnRole,
    ExcelColumnWidthMode,
    ExcelDerivedRowDef,
    ExcelDerivedRowKind,
    ExcelDetailColumnOptions,
    DetailAggregate,
    ExcelMeasureScale,
    ExcelNumberFormat,
    ExcelTotalPosition,
    ExtraBreakdown,
    MetricSection,
    DataModel,
    ReportConfig,
    ReportConfigurationExcelLayoutTemplateDto,
    SectionExcelLayout,
} from "../../types";
import {
    buildExcelLayoutPreview,
    breakdownColumnForModel,
    columnMatchesKey,
    dataColumnSelectionKey,
    excelSheetKey,
    excelSheetsInUse,
    metricPositionWithinSheet,
    metricSheetName,
    excelLayoutValidationMessages,
    excelSectionAliases,
    isPeriodDateExtra,
    makeDefaultExcelDetailColumnOptions,
    makeDefaultExcelLayout,
    moveExcelDetailColumn,
    reconcileExcelLayoutColumns,
    storedExcelSheetName,
    validateExcelSheetName,
    usesCombinedRuleSets,
    combinedRows,
    combinedRowLabel,
    DETAIL_AGGREGATES,
    allowedDetailAggregates,
    defaultFormatForAggregate,
    detailAggregateDef,
    detailAggregateHeader,
    detailAggregateIssues,
    detailAggregateOf,
    detailOutputValueType,
    usesDetailAggregates,
} from "../../reportConfigurationLogic";
import { ReportConfigSelect } from "../ReportConfigSelect";
import type { ReportValidationIssue } from "../../validation/issues";
import { ChartSettingsEditor } from "./ChartSettingsEditor";
import { ComboChartEditor } from "./ComboChartEditor";
import { ComboChartPreview } from "./ComboChartPreview";
import { comboSeriesSuggestions, isComboChart, makeDefaultComboChartSettings, normalizeComboChartSettings, seedComboSeries } from "../../logic/chartCombo";

export { clearExcelLayoutTemplateCache } from "../../api/excelLayoutTemplates";

// Local id generator for new ExtraBreakdown/derived-row ids created from this editor.
// Not exported from reportConfigurationLogic.ts, so this stays self-contained here -
// only needs to be unique within one browser session, same as the ids created there.
let localIdSeq = 1;
const nextLocalId = (prefix: string) => `${prefix}_${Date.now().toString(36)}_${localIdSeq++}`;

const ROLE_OPTIONS: { value: ExcelColumnRole; label: string }[] = [
    { value: "ignore", label: "Not on sheet" },
    { value: "rowLabel", label: "Row Label" },
    { value: "columnAxis", label: "Time Axis (columns)" },
    { value: "measure", label: "Value" },
];

const NUMBER_FORMAT_OPTIONS: { value: ExcelNumberFormat; label: string }[] = [
    { value: "accountingInt", label: "Accounting (whole number)" },
    { value: "integer", label: "Integer" },
    { value: "percent0", label: "Percent (0 decimals)" },
    { value: "percent2", label: "Percent (2 decimals)" },
    { value: "decimal2", label: "Decimal (2 places)" },
];

const DEFAULT_DERIVED_ROW_NUMBER_FORMAT: ExcelNumberFormat = "accountingInt";

const SCALE_OPTIONS: { value: ExcelMeasureScale; label: string }[] = [
    { value: "raw", label: "As returned" },
    { value: "divide100", label: "Divide by 100 (SQL returns 0-100)" },
];

const TOTAL_POSITION_OPTIONS: { value: ExcelTotalPosition; label: string }[] = [
    { value: "bottom", label: "Bottom" },
    { value: "top", label: "Top" },
    { value: "none", label: "No total row" },
];

const CHART_TYPE_OPTIONS: { value: ExcelChartType; label: string }[] = [
    { value: "none", label: "No chart" },
    { value: "stackedColumn", label: "Stacked column" },
    { value: "clusteredColumn", label: "Clustered column" },
    { value: "line", label: "Line" },
];

const CHART_MODE_OPTIONS: { value: ExcelChartMode; label: string; hint: string }[] = [
    { value: "single", label: "One type", hint: "Every series uses the chart type above." },
    { value: "combo", label: "Combo", hint: "Pick column or line, and left or right axis, per series." },
];

const DERIVED_ROW_KIND_OPTIONS: { value: ExcelDerivedRowKind; label: string }[] = [
    { value: "sumOf", label: "Sum of rows" },
    { value: "ratio", label: "Ratio (A / B)" },
    { value: "difference", label: "Difference (A - B)" },
    { value: "remainderOf", label: "Remainder (A minus B, C, ...)" },
    { value: "constant", label: "Constant / benchmark value" },
];

// Detail columns have no DataType from the catalog to pick a sensible format for, so
// every option is offered in one list and the user picks (defaults are inferred from
// the model's timestampColumns bucket - see makeDefaultExcelDetailColumnOptions).
const DETAIL_FORMAT_OPTIONS: { value: ExcelCellFormat; label: string }[] = [
    { value: "text", label: "Text" },
    { value: "accountingInt", label: "Accounting (whole number)" },
    { value: "integer", label: "Integer" },
    { value: "percent0", label: "Percent (0 decimals)" },
    { value: "percent2", label: "Percent (2 decimals)" },
    { value: "decimal2", label: "Decimal (2 places)" },
    { value: "MM-dd-yyyy", label: "Date (MM-DD-YYYY)" },
    { value: "MM-dd-yyyy hh:mm AM/PM", label: "Date & Time (MM-DD-YYYY hh:mm AM/PM)" },
    { value: "yyyy-MM-dd", label: "Date (YYYY-MM-DD)" },
    { value: "hh:mm AM/PM", label: "Time (hh:mm AM/PM)" },
    { value: "raw", label: "As returned" },
];

const DETAIL_WIDTH_MODE_OPTIONS: { value: ExcelColumnWidthMode; label: string }[] = [
    { value: "auto", label: "Auto-fit" },
    { value: "fixed", label: "Fixed width" },
];

const NEW_SHEET_OPTION = "__new_excel_sheet__";

type ExcelLayoutTemplateDefinition = Partial<SectionExcelLayout> & {
    extraBreakdowns?: ExtraBreakdown[];
};

function defaultMeasureOptions(section: MetricSection) {
    return makeDefaultExcelMeasure(section.displayAs);
}

function applyRoleDefaults(column: ExcelColumnLayout, role: ExcelColumnRole, section: MetricSection, granularity?: DateGranularity): ExcelColumnLayout {
    if (role === "rowLabel") return { ...column, role, rowLabel: column.rowLabel || defaultRowLabelOptions(column.label) };
    if (role === "columnAxis") return { ...column, role, columnAxis: column.columnAxis || defaultColumnAxisOptions(granularity || "month") };
    if (role === "measure") return { ...column, role, measure: column.measure || defaultMeasureOptions(section) };
    return { ...column, role };
}

interface ExcelLayoutEditorProps {
    report: ReportConfig;
    section: MetricSection;
    onChange: (updater: (current: MetricSection) => MetricSection) => void;
    dataModelId?: number;
    dataModelColumnId?: number;
    templateRefreshToken?: number;
    showTemplatePicker?: boolean;
    showTemplateActions?: boolean;
    showIncludeToggle?: boolean;
    showClearLayout?: boolean;
    showReportLayoutFields?: boolean;
    headingPrefix?: string;
    dataModels?: Record<string, DataModel>;
    validationIssues?: ReportValidationIssue[];
}

interface ExcelLayoutTemplatePickerProps {
    report: ReportConfig;
    section: MetricSection;
    onChange: (updater: (current: MetricSection) => MetricSection) => void;
    dataModelId?: number;
    dataModelColumnId?: number;
    templateRefreshToken?: number;
}

function ExcelIconButton({ icon, label, onClick, tone = "default", disabled = false }: { icon: string; label: string; onClick: () => void; tone?: "default" | "danger"; disabled?: boolean }) {
    return (
        <button className={`rc-excel-icon-action ${tone === "danger" ? "danger" : ""}`} type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick}>
            <span className="material-icons md-16" aria-hidden="true">{icon}</span>
        </button>
    );
}

/** What a Full Row Detail sheet column is, for the small tag next to its name. */
function detailColumnKind(sourceAlias: string, valueType: string): { text: string; tone: string } {
    const typeName = valueType === "number" ? "Number" : valueType === "date" ? "Date" : "Text";
    if (sourceAlias.startsWith("detail:const:")) return { text: "Fixed value", tone: "muted" };
    if (sourceAlias.startsWith("detail:ruleSet:")) return { text: "Categorization", tone: "type" };
    if (sourceAlias.startsWith("detail:calc:")) return { text: `Calculated · ${typeName}`, tone: "calc" };
    return { text: typeName, tone: "type" };
}

function parseExcelLayoutPreview(preview: string) {
    const lines = preview.split(/\r?\n/);
    const title = lines[0] ?? "Workbook Preview";
    const tableLines: string[] = [];
    let chart = "";
    let notesMode = false;
    const notes: string[] = [];

    lines.slice(1).forEach((line) => {
        if (!line.trim()) return;
        if (line.startsWith("Chart:")) {
            chart = line.replace(/^Chart:\s*/i, "");
            notesMode = false;
            return;
        }
        if (line.startsWith("Needs attention:")) {
            notesMode = true;
            return;
        }
        if (notesMode) {
            notes.push(line.replace(/^-\s*/, ""));
            return;
        }
        tableLines.push(line);
    });

    const headers = tableLines[0]?.split("\t").filter(Boolean) || [];
    const rows = tableLines.slice(1).map((line) => line.split("\t"));

    return { title, headers, rows, chart, notes };
}

function ExcelLayoutPreview({ preview, chart, ratioRowLabels }: { preview: string; chart?: ExcelChartLayout; ratioRowLabels?: string[] }) {
    const model = parseExcelLayoutPreview(preview);
    const showComboPreview = !!model.chart && !!chart?.enabled && isComboChart(chart);

    if (!model.headers.length) {
        return (
            <div className="rc-excel-preview-empty">
                <span className="material-icons md-18" aria-hidden="true">visibility_off</span>
                <span>{model.title}</span>
            </div>
        );
    }

    const chartType = model.chart.split(",")[0]?.trim() || "Chart";
    const chartSeries = model.chart.includes("series =") ? model.chart.split("series =")[1]?.trim() : "";
    const chartKind = chartType.toLowerCase().includes("line")
        ? "line"
        : chartType.toLowerCase().includes("pie")
            ? "pie"
            : chartType.toLowerCase().includes("cluster")
                ? "clustered"
                : "stacked";
    const renderPreviewCell = (value: string, isLabelCell: boolean) => {
        if (isLabelCell) return value === "..." ? <span className="rc-excel-muted-cell">...</span> : value;
        if (value === "#") return <span className="rc-excel-sample-value">#</span>;
        return value;
    };

    return (
        <div className="rc-excel-preview-shell">
            <div className="rc-excel-preview-sheet">
                <div className="rc-excel-preview-titlebar">
                    <span className="material-icons md-18" aria-hidden="true">table_chart</span>
                    {model.title.trim() && <span>{model.title}</span>}
                    <span className="rc-excel-preview-badge">Workbook preview</span>
                </div>
                <div className="rc-excel-preview-table-wrap">
                    <table className="rc-excel-preview-table">
                        <thead>
                            <tr>{model.headers.map((header, index) => <th key={`${header}_${index}`}>{header}</th>)}</tr>
                        </thead>
                        <tbody>
                            {model.rows.map((row, rowIndex) => {
                                const isTotal = /^total$/i.test(row[0] || "");
                                const isDerived = row.length < model.headers.length && row[1]?.startsWith("[");
                                return (
                                    <tr key={`${row[0]}_${rowIndex}`} className={`${isTotal ? "total" : ""} ${isDerived ? "derived" : ""}`.trim()}>
                                        {isDerived ? (
                                            <>
                                                <td>{row[0]}</td>
                                                <td colSpan={Math.max(model.headers.length - 1, 1)}>
                                                    <span className="rc-excel-formula-chip">{row[1]}</span>
                                                </td>
                                            </>
                                        ) : (
                                            model.headers.map((_, cellIndex) => (
                                                <td key={cellIndex}>{renderPreviewCell(row[cellIndex] || "", cellIndex === 0)}</td>
                                            ))
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {showComboPreview && chart && (
                <div className="rc-excel-preview-chart rc-excel-preview-chart-combo">
                    <div>
                        <span className="material-icons md-18" aria-hidden="true">insert_chart</span>
                        <strong>Combo chart</strong>
                    </div>
                    <ComboChartPreview chart={chart} categories={model.headers.slice(1)} ratioRowLabels={ratioRowLabels} />
                </div>
            )}

            {model.chart && !showComboPreview && (
                <div className="rc-excel-preview-chart">
                    <div>
                        <span className="material-icons md-18" aria-hidden="true">stacked_bar_chart</span>
                        <strong>{chartType}</strong>
                        {chartSeries && <small>Series: {chartSeries}</small>}
                    </div>
                    <div className={`rc-excel-chart-visual ${chartKind}`} aria-hidden="true">
                        {chartKind === "line" ? (
                            <svg viewBox="0 0 220 110" role="presentation" focusable="false">
                                <polyline points="10,82 52,62 92,70 135,34 178,48 212,22" />
                                <circle cx="10" cy="82" r="4" />
                                <circle cx="52" cy="62" r="4" />
                                <circle cx="92" cy="70" r="4" />
                                <circle cx="135" cy="34" r="4" />
                                <circle cx="178" cy="48" r="4" />
                                <circle cx="212" cy="22" r="4" />
                            </svg>
                        ) : chartKind === "pie" ? (
                            <span className="rc-excel-pie-preview" />
                        ) : (
                            <>
                                <span style={{ height: "42%" }}><i /><i /><i /></span>
                                <span style={{ height: "66%" }}><i /><i /><i /></span>
                                <span style={{ height: "52%" }}><i /><i /><i /></span>
                                <span style={{ height: "78%" }}><i /><i /><i /></span>
                                <span style={{ height: "58%" }}><i /><i /><i /></span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {model.notes.length > 0 && (
                <div className="rc-excel-preview-notes">
                    <span className="material-icons md-18" aria-hidden="true">tips_and_updates</span>
                    <div>
                        <strong>Needs attention</strong>
                        {model.notes.map((note, index) => <p key={index}>{note}</p>)}
                    </div>
                </div>
            )}
        </div>
    );
}

function templateNameForTitle(title: string) {
    return `Template - ${(title || "Untitled Metric").trim() || "Untitled Metric"}`;
}

function normalizeTemplateName(value: string) {
    return value.trim().toLowerCase();
}

function normalizeTemplateLayout(parsed: Partial<SectionExcelLayout>, section: MetricSection, report: ReportConfig): SectionExcelLayout {
    const base = section.excelLayout ? reconcileExcelLayoutColumns(section, section.excelLayout, report) : makeDefaultExcelLayout(section, report);
    return reconcileExcelLayoutColumns(section, {
        ...base,
        ...parsed,
        enabled: parsed.enabled ?? base.enabled,
        sheetName: base.sheetName,
        title: base.title,
        blockOrder: base.blockOrder,
        columns: Array.isArray(parsed.columns) ? parsed.columns : base.columns,
        derivedRows: Array.isArray(parsed.derivedRows) ? parsed.derivedRows : base.derivedRows,
        chart: { ...base.chart, ...(parsed.chart || {}) },
    }, report);
}

function remapTemplateLayoutExtraIds(parsed: Partial<SectionExcelLayout>, idMap: Map<string, string>) {
    if (!idMap.size || !Array.isArray(parsed.columns)) return parsed;
    return {
        ...parsed,
        columns: parsed.columns.map((column) => {
            const mappedExtraId = column.extraBreakdownId ? idMap.get(column.extraBreakdownId) : undefined;
            const mappedSourceAlias = column.sourceAlias?.startsWith("extra:")
                ? `extra:${idMap.get(column.sourceAlias.slice("extra:".length)) || column.sourceAlias.slice("extra:".length)}`
                : column.sourceAlias;
            return {
                ...column,
                sourceAlias: mappedSourceAlias,
                extraBreakdownId: mappedExtraId || column.extraBreakdownId,
            };
        }),
    };
}

function normalizeDerivedRow(row: ExcelDerivedRowDef): ExcelDerivedRowDef {
    return { ...row, numberFormat: row.numberFormat || DEFAULT_DERIVED_ROW_NUMBER_FORMAT };
}

function applyTemplateBreakdowns(section: MetricSection, templateBreakdowns?: ExtraBreakdown[]) {
    const idMap = new Map<string, string>();
    if (!Array.isArray(templateBreakdowns) || !templateBreakdowns.length) {
        return { section, idMap };
    }

    const nextBreakdowns = [...section.extraBreakdowns];
    templateBreakdowns.forEach((breakdown) => {
        // A Period Date part carries no column. It maps onto this section's own Period Date
        // part of the same granularity - and only when the section has a Period Date, since
        // the template does not carry the date rules themselves.
        if (isPeriodDateExtra(breakdown)) {
            if (!section.periodDate || !breakdown.granularity) return;
            const existingIndex = nextBreakdowns.findIndex((existing) => isPeriodDateExtra(existing) && existing.granularity === breakdown.granularity);
            const nextId = existingIndex >= 0 ? nextBreakdowns[existingIndex].id : breakdown.id || nextLocalId("extra");
            idMap.set(breakdown.id, nextId);
            if (existingIndex < 0) nextBreakdowns.push({ ...breakdown, id: nextId, column: "", groups: [] });
            return;
        }
        if (!breakdown?.column) return;
        const templateColumn = breakdownColumnForModel(section.model, breakdown.column);
        const existingIndex = nextBreakdowns.findIndex((existing) => {
            const existingColumn = breakdownColumnForModel(section.model, existing.column);
            return templateColumn
                ? columnMatchesKey(templateColumn, existing.column)
                : columnMatchesKey(existingColumn, breakdown.column);
        });
        const nextId = existingIndex >= 0 ? nextBreakdowns[existingIndex].id : breakdown.id || nextLocalId("extra");
        idMap.set(breakdown.id, nextId);

        const nextBreakdown = {
            ...breakdown,
            id: nextId,
            column: templateColumn ? dataColumnSelectionKey(templateColumn) : breakdown.column,
        };

        if (existingIndex >= 0) {
            nextBreakdowns[existingIndex] = nextBreakdown;
        } else {
            nextBreakdowns.push(nextBreakdown);
        }
    });

    return { section: { ...section, extraBreakdowns: nextBreakdowns }, idMap };
}

function inferTemplateBreakdownsFromLayout(section: MetricSection, parsed: Partial<SectionExcelLayout>) {
    if (!Array.isArray(parsed.columns)) return [];
    return parsed.columns.reduce<ExtraBreakdown[]>((result, column) => {
        const templateExtraId = column.extraBreakdownId || (column.sourceAlias?.startsWith("extra:") ? column.sourceAlias.slice("extra:".length) : "");
        if (!templateExtraId) return result;

        const label = String(column.label || "").replace(/\s*\((month|year|quarter|week|day)\)\s*$/i, "");
        const matchedColumn = breakdownColumnForModel(section.model, label || templateExtraId);
        if (!matchedColumn) return result;

        result.push({
            id: templateExtraId,
            column: dataColumnSelectionKey(matchedColumn),
            groups: [],
            granularity: column.columnAxis?.granularity,
        });
        return result;
    }, []);
}

export function ExcelLayoutTemplatePicker({ report, section, onChange, dataModelId, dataModelColumnId, templateRefreshToken = 0 }: ExcelLayoutTemplatePickerProps) {
    const [templates, setTemplates] = useState<ReportConfigurationExcelLayoutTemplateDto[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState("");

    useEffect(() => {
        if (!dataModelId || !dataModelColumnId) {
            setTemplates([]);
            setTemplatesError("");
            setTemplatesLoading(false);
            return;
        }

        let isCurrent = true;
        setTemplatesLoading(true);
        setTemplatesError("");

        loadTemplates(dataModelId, dataModelColumnId, section.displayAs, report.reportId)
            .then((response) => {
                if (!isCurrent) return;
                setTemplates(response);
            })
            .catch((error) => {
                if (!isCurrent) return;
                console.log(error);
                setTemplates([]);
                setTemplatesError("Templates could not be loaded.");
            })
            .finally(() => {
                if (isCurrent) setTemplatesLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [dataModelId, dataModelColumnId, section.displayAs, report.reportId, templateRefreshToken]);

    function applyTemplate(templateIdValue: string) {
        if (!templateIdValue) {
            onChange((current) => {
                const existing = current.excelLayout;
                const clean = makeDefaultExcelLayout(current, report);
                const title = existing?.title ?? clean.title;
                return {
                    ...current,
                    excelLayout: {
                        ...clean,
                        enabled: existing?.enabled ?? clean.enabled,
                        sheetName: existing?.sheetName ?? clean.sheetName,
                        title,
                        blockOrder: existing?.blockOrder ?? clean.blockOrder,
                        templateId: null,
                        templateName: templateNameForTitle(title),
                        templateNameTouched: false,
                        sourceTemplateName: undefined,
                        sourceTemplateIsGlobal: undefined,
                        saveAsTemplate: false,
                        templateSaveMode: "create",
                    },
                };
            });
            return;
        }

        const templateId = Number(templateIdValue);
        const template = templates.find((item) => item.templateId === templateId);
        if (!template) return;

        try {
            const parsed = JSON.parse(template.templateDefinitionJson || "{}") as ExcelLayoutTemplateDefinition;
            onChange((current) => {
                const { extraBreakdowns: templateBreakdowns, ...layoutDefinition } = parsed;
                const resolvedTemplateBreakdowns = Array.isArray(templateBreakdowns) && templateBreakdowns.length
                    ? templateBreakdowns
                    : inferTemplateBreakdownsFromLayout(current, layoutDefinition);
                const { section: sectionWithTemplateBreakdowns, idMap } = applyTemplateBreakdowns(current, resolvedTemplateBreakdowns);
                const nextLayout = normalizeTemplateLayout(remapTemplateLayoutExtraIds(layoutDefinition, idMap), sectionWithTemplateBreakdowns, report);
                const existingSheetName = current.excelLayout?.sheetName ?? "";
                return {
                    ...sectionWithTemplateBreakdowns,
                    excelLayout: {
                        ...nextLayout,
                        sheetName: existingSheetName,
                        templateId: template.templateId,
                        templateName: template.templateName,
                        templateNameTouched: true,
                        sourceTemplateName: template.templateName,
                        sourceTemplateIsGlobal: !!template.isGlobalTemplate,
                        saveAsTemplate: false,
                        templateSaveMode: "create",
                    },
                };
            });
        } catch (error) {
            console.log(error);
            setTemplatesError("The selected template has invalid saved JSON.");
        }
    }

    return (
        <div className="rc-excel-header-template-wrap" onMouseDown={(event) => event.stopPropagation()}>
            <label className="rc-label-block rc-excel-header-template-picker">
                Load Layout from templates
                <ReportConfigSelect<string>
                    value={section.excelLayout?.templateId ? String(section.excelLayout.templateId) : ""}
                    options={[
                        { value: "", label: templatesLoading ? "Loading templates..." : templates.length ? "Select a template..." : "No saved templates", disabled: templatesLoading || !templates.length },
                        ...templates.map((template) => ({ value: String(template.templateId), label: template.templateName })),
                    ]}
                    ariaLabel="Load layout from templates"
                    onChange={applyTemplate}
                />
            </label>
            {templatesError && <span className="rc-excel-template-error">{templatesError}</span>}
        </div>
    );
}

export function ExcelLayoutEditor({
    report,
    section,
    onChange,
    dataModelId,
    dataModelColumnId,
    templateRefreshToken = 0,
    showTemplatePicker = true,
    showTemplateActions = true,
    showIncludeToggle = true,
    showClearLayout = true,
    showReportLayoutFields = true,
    headingPrefix = "Excel Layout",
    dataModels = {},
    validationIssues = [],
}: ExcelLayoutEditorProps) {
    const layout = section.excelLayout;
    const aliases = useMemo(() => excelSectionAliases(section, report), [section, report]);
    const problems = layout ? excelLayoutValidationMessages(section) : [];
    const preview = layout ? buildExcelLayoutPreview(section) : "";
    const blockValidationIssues = validationIssues.filter((issue) => issue.tab === "excel" && issue.sectionId === section.id);
    const hasColumnAxisSource = aliases.some((item) => item.extraBreakdownId && section.extraBreakdowns.some((extra) => extra.id === item.extraBreakdownId && extra.granularity));
    const defaultChartDataOrientation: ExcelChartDataOrientation = layout?.columns.some((column) => column.role === "columnAxis")
        ? "rowsAsSeries"
        : "rowsAsCategories";
    const effectiveTitle = layout?.title ?? section.name;
    const effectiveTemplateName = layout?.templateName || templateNameForTitle(effectiveTitle);
    // Combined tables hide the template controls, so a leftover template link must not lock them.
    const isTemplateLocked = showTemplateActions && !!layout?.templateId && !layout?.saveAsTemplate && !usesCombinedRuleSets(section);
    const shouldRenameTemplateBeforeSave = !!layout?.saveAsTemplate
        && !!layout?.templateId
        && layout?.templateSaveMode !== "update"
        && normalizeTemplateName(effectiveTemplateName) === normalizeTemplateName(layout?.sourceTemplateName || "");
    const sheetSummaries = useMemo(() => excelSheetsInUse(report), [report]);
    const currentSheetName = metricSheetName(report, section);
    const sheetPosition = metricPositionWithinSheet(report, section.id);
    const [addingSheet, setAddingSheet] = useState(false);
    const [newSheetName, setNewSheetName] = useState("");
    const newSheetProblem = addingSheet ? validateExcelSheetName(newSheetName) : null;
    const duplicateSheet = addingSheet && sheetSummaries.some((sheet) => sheet.key === excelSheetKey(newSheetName));
    const newSheetAssignmentProblem = addingSheet && sheetPosition?.total === 1
        ? "This metric is the only block on its current worksheet. Rename the worksheet instead, or move another metric here before creating a new worksheet."
        : "";
    const sheetOptions = [
        ...sheetSummaries.map((sheet) => ({
            value: sheet.name,
            label: `${sheet.name} - ${sheet.enabledCount}/${sheet.totalCount} block${sheet.totalCount === 1 ? "" : "s"}`,
        })),
        { value: NEW_SHEET_OPTION, label: "+ New worksheet..." },
    ];

    const isCombo = !!layout && isComboChart(layout.chart);
    const comboSuggestions = useMemo(() => comboSeriesSuggestions(section, layout, report), [section, layout, report]);

    // Switching to combo forces "table rows are series" (the only orientation a per-series table
    // makes sense for) and seeds the table on first use. Switching back keeps the table in the
    // draft, so the user can return to combo without losing work.
    function setChartMode(mode: ExcelChartMode) {
        updateLayout((current) => {
            if (mode === "single") return { ...current, chart: { ...current.chart, mode: "single" } };
            const existing = current.chart.combo ? normalizeComboChartSettings(current.chart.combo) : makeDefaultComboChartSettings();
            const combo = existing.series.length ? existing : { ...existing, series: seedComboSeries(comboSeriesSuggestions(section, current, report), current) };
            return {
                ...current,
                chart: {
                    ...current.chart,
                    mode: "combo",
                    dataOrientation: "rowsAsSeries",
                    combo,
                    settings: { ...current.chart.settings!, showLegend: true },
                },
            };
        });
    }

    function updateLayout(updater: (current: SectionExcelLayout) => SectionExcelLayout) {
        onChange((current) => {
            const base = current.excelLayout ? reconcileExcelLayoutColumns(current, current.excelLayout, report) : makeDefaultExcelLayout(current, report);
            return { ...current, excelLayout: updater(base) };
        });
    }

    function chooseSheet(name: string) {
        if (excelSheetKey(name) !== excelSheetKey(currentSheetName) && sheetPosition?.total === 1) {
            reportToast.warning(
                "Worksheet would be removed",
                "This metric is the only block on its current worksheet. Rename or delete the worksheet explicitly instead.",
            );
            return;
        }
        updateLayout((current) => ({ ...current, sheetName: storedExcelSheetName(report, name) }));
    }

    function handleSheetChange(value: string) {
        if (value === NEW_SHEET_OPTION) {
            setAddingSheet(true);
            const modelLabel = section.model && dataModels[section.model]?.label ? dataModels[section.model].label : section.model || "Sheet";
            setNewSheetName(`${modelLabel} - `.slice(0, 31));
            return;
        }
        setAddingSheet(false);
        chooseSheet(value);
    }

    function confirmNewSheet() {
        if (validateExcelSheetName(newSheetName) || duplicateSheet || newSheetAssignmentProblem) return;
        chooseSheet(newSheetName);
        setAddingSheet(false);
        setNewSheetName("");
    }

    function updateColumn(sourceAlias: string, updater: (column: ExcelColumnLayout) => ExcelColumnLayout) {
        updateLayout((current) => ({
            ...current,
            columns: current.columns.map((column) => (column.sourceAlias === sourceAlias ? updater(column) : column)),
        }));
    }

    // Detail-column counterpart to updateColumn: merges into the column's `detail`
    // options block, materializing sensible defaults first if this is the column's
    // first edit (mirrors how the pivot roles lazily default via applyRoleDefaults).
    function updateDetailColumn(sourceAlias: string, updater: (detail: ExcelDetailColumnOptions) => ExcelDetailColumnOptions) {
        updateColumn(sourceAlias, (current) => {
            const selectionKey = sourceAlias.startsWith("detail:") ? sourceAlias.slice("detail:".length) : sourceAlias;
            const baseDetail = current.detail || makeDefaultExcelDetailColumnOptions(section.model, selectionKey, current.label);
            return { ...current, role: "detailColumn" as ExcelColumnRole, detail: updater(baseDetail) };
        });
    }

    function moveDetailColumn(sourceAlias: string, direction: -1 | 1) {
        updateLayout((current) => moveExcelDetailColumn(current, sourceAlias, direction));
    }

    function addTimeAxis(granularity: DateGranularity) {
        const basedOnKey = report.modelBasedOn[section.model] || "";
        // With a Period Date the time axis follows it, so the sheet's months are the same
        // months the metric is grouped and filtered by.
        const usePeriodDate = !!section.periodDate;
        if (!basedOnKey && !usePeriodDate) return;
        onChange((current) => {
            // Reuse the Period Date part if the metric already groups by it, rather than
            // adding the same date part twice.
            const existing = usePeriodDate ? current.extraBreakdowns.find((extra) => isPeriodDateExtra(extra) && extra.granularity === granularity) : undefined;
            const newExtra: ExtraBreakdown = existing
                || (usePeriodDate
                    ? { id: nextLocalId("extra"), column: "", groups: [], granularity, source: "periodDate" }
                    : { id: nextLocalId("extra"), column: basedOnKey, groups: [], granularity });
            const nextSection: MetricSection = existing ? current : { ...current, extraBreakdowns: [...current.extraBreakdowns, newExtra] };
            const baseLayout = nextSection.excelLayout ? reconcileExcelLayoutColumns(nextSection, nextSection.excelLayout, report) : makeDefaultExcelLayout(nextSection, report);
            const columns = baseLayout.columns.map((column) =>
                column.extraBreakdownId === newExtra.id
                    ? { ...column, role: "columnAxis" as ExcelColumnRole, columnAxis: defaultColumnAxisOptions(granularity) }
                    : column,
            );
            return { ...nextSection, excelLayout: { ...baseLayout, columns, enabled: true } };
        });
    }

    function addDerivedRow() {
        const newRow: ExcelDerivedRowDef = { id: nextLocalId("derived"), kind: "sumOf", label: "New Row", sourceRowLabels: [], numberFormat: DEFAULT_DERIVED_ROW_NUMBER_FORMAT };
        updateLayout((current) => ({ ...current, derivedRows: [...current.derivedRows, newRow] }));
    }

    function updateDerivedRow(id: string, updater: (row: ExcelDerivedRowDef) => ExcelDerivedRowDef) {
        updateLayout((current) => ({ ...current, derivedRows: current.derivedRows.map((row) => (row.id === id ? normalizeDerivedRow(updater(normalizeDerivedRow(row))) : row)) }));
    }

    function removeDerivedRow(id: string) {
        updateLayout((current) => ({ ...current, derivedRows: current.derivedRows.filter((row) => row.id !== id) }));
    }

    function clearLayout() {
        onChange((current) => {
            const clean = makeDefaultExcelLayout(current, report);
            return {
                ...current,
                excelLayout: {
                    ...clean,
                    sheetName: current.excelLayout?.sheetName ?? clean.sheetName,
                    templateId: null,
                    templateName: templateNameForTitle(clean.title || current.name),
                    templateNameTouched: false,
                    sourceTemplateName: undefined,
                    sourceTemplateIsGlobal: undefined,
                    saveAsTemplate: false,
                    templateSaveMode: "create",
                },
            };
        });
    }

    if (!section.model || !section.breakdownColumn) {
        return <p className="rc-hint">Pick a breakdown column for this metric first, then come back here to lay it out on the Excel sheet.</p>;
    }

    const isDetailMode = section.displayAs === "detail";
    // Combined rule sets: rows come from the Metrics tab (MetricSection.combinedRows); this tab
    // only formats them. Excel layout templates are keyed on a breakdown column, which a
    // combined table doesn't use, so they are not offered for it.
    const isCombinedMode = !isDetailMode && usesCombinedRuleSets(section);
    const combinedRowHeader = layout?.columns.find((column) => column.role === "rowLabel")?.rowLabel?.displayLabel ?? "Category";
    const workbookAliases = isCombinedMode ? aliases.filter((item) => item.extraBreakdownId) : aliases;
    // Detail column order lives in layout.columns once a layout exists; before that,
    // fall back to selection order (from excelSectionAliases) so the list has something
    // to show and to move, same lazy-default pattern updateLayout already uses.
    const detailColumns: ExcelColumnLayout[] = isDetailMode
        ? (layout?.mode === "detail" ? layout.columns : aliases.map((item) => ({ sourceAlias: item.sourceAlias, label: item.label, role: "detailColumn" as ExcelColumnRole })))
        : [];
    // Aggregated sheet columns group the detail query (logic/detailAggregates.ts).
    const aggregating = isDetailMode && usesDetailAggregates(section);
    const aggregateIssues = isDetailMode ? detailAggregateIssues(report, section) : [];
    const onSheetColumns = detailColumns.filter((column) => column.detail?.include ?? true);
    const onSheetCount = onSheetColumns.length;
    const aggregatedCount = onSheetColumns.filter((column) => detailAggregateOf(column) !== "none").length;
    const groupedCount = onSheetColumns.filter((column) => detailAggregateOf(column) === "none" && !column.sourceAlias.startsWith("detail:const:")).length;

    function includeAllDetailColumns() {
        updateLayout((current) => ({
            ...current,
            columns: current.columns.map((column) => {
                if (column.role !== "detailColumn" && !column.sourceAlias.startsWith("detail:")) return column;
                const selectionKey = column.sourceAlias.startsWith("detail:") ? column.sourceAlias.slice("detail:".length) : column.sourceAlias;
                const detail = column.detail || makeDefaultExcelDetailColumnOptions(section.model, selectionKey, column.label);
                return { ...column, role: "detailColumn" as ExcelColumnRole, detail: { ...detail, include: true } };
            }),
        }));
    }

    return (
        <section className="rc-block rc-excel-layout" data-rc-validation={`excel-metric-${section.id}`}>
            <div className="rc-excel-layout-head">
                <div className="rc-excel-layout-intro">
                    <h4>{headingPrefix} - {section.name || "Untitled metric"}</h4>
                    {showTemplatePicker && !isCombinedMode && (
                        <ExcelLayoutTemplatePicker
                            report={report}
                            section={section}
                            onChange={onChange}
                            dataModelId={dataModelId}
                            dataModelColumnId={dataModelColumnId}
                            templateRefreshToken={templateRefreshToken}
                        />
                    )}
                    <p className="rc-hint">Configure how this metric appears in the generated workbook.</p>
                    {blockValidationIssues.length > 0 && (
                        <ul className="rc-excel-layout-problems rc-excel-validation-issues" aria-label="Excel layout validation issues">
                            {blockValidationIssues.map((issue) => (
                                <li key={issue.id} className={issue.severity}>
                                    <span className="material-icons md-16" aria-hidden="true">{issue.severity === "error" ? "error" : "warning"}</span>
                                    <span>
                                        <strong>{issue.severity === "error" ? "Required: " : "Warning: "}{issue.title}</strong>
                                        <small>{issue.message}</small>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="rc-excel-header-toggles">
                    {showTemplateActions && !isCombinedMode && (
                        <label className="rc-checkbox-row rc-excel-enable">
                            <input
                                type="checkbox"
                                checked={!!layout?.saveAsTemplate}
                                disabled={!dataModelId || !dataModelColumnId}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    if (checked && layout?.templateId && layout.templateSaveMode !== "update") {
                                        reportToast.warning(
                                            "Template name required",
                                            layout.sourceTemplateIsGlobal
                                                ? "Change the template name to save a report-level template. Global templates can only be updated from Global Templates."
                                                : "Change the template name to save a new reusable layout, or check Update Selected Template.",
                                        );
                                    }
                                    updateLayout((current) => ({
                                        ...current,
                                        saveAsTemplate: checked,
                                        templateName: current.templateName || templateNameForTitle(current.title || section.name),
                                        templateSaveMode: checked ? current.templateSaveMode || "create" : "create",
                                    }));
                                }}
                            />
                            <span className="rc-excel-check-box" aria-hidden="true" />
                            <span>Save as Template</span>
                        </label>
                    )}
                    {showIncludeToggle && (
                        <label className="rc-checkbox-row rc-excel-enable">
                            <input
                                type="checkbox"
                                checked={!!layout?.enabled}
                                onChange={(event) => updateLayout((current) => ({ ...current, enabled: event.target.checked }))}
                            />
                            <span className="rc-excel-check-box" aria-hidden="true" />
                            <span>Include in Workbook</span>
                        </label>
                    )}
                    {showClearLayout && (
                        <button className="rc-excel-clear-layout" type="button" aria-label="Clear layout" title="Clear layout" onClick={clearLayout}>
                            <span className="material-icons md-16" aria-hidden="true">restart_alt</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="rc-excel-control-grid">
                {!!layout?.saveAsTemplate && (
                    <div className="rc-excel-control-row rc-excel-control-row-single">
                        <label className="rc-label-block rc-excel-template-name-field" data-rc-validation={`excel-template-${section.id}`}>
                            Template Name
                            <input
                                value={effectiveTemplateName}
                                disabled={layout?.templateSaveMode === "update"}
                                onChange={(event) => updateLayout((current) => ({ ...current, templateName: event.target.value, templateNameTouched: true }))}
                            />
                            {shouldRenameTemplateBeforeSave && (
                                <span className="rc-excel-template-name-warning">
                                    Change the template name to create a new reusable template.
                                </span>
                            )}
                        </label>
                        {layout?.templateId ? (
                            <div className="rc-excel-template-update-action">
                                <span className="rc-excel-template-update-spacer" aria-hidden="true">&nbsp;</span>
                                <label
                                    className={`rc-checkbox-row rc-excel-update-template-check${layout.sourceTemplateIsGlobal ? " disabled" : ""}`}
                                    title={layout.sourceTemplateIsGlobal ? "Global templates can only be updated from the Global Templates tab." : undefined}
                                >
                                    <input
                                        type="checkbox"
                                        checked={layout.templateSaveMode === "update"}
                                        disabled={!!layout.sourceTemplateIsGlobal}
                                        onChange={(event) => {
                                            const checked = event.target.checked;
                                            updateLayout((current) => ({ ...current, templateSaveMode: checked ? "update" : "create" }));
                                            if (checked) {
                                                reportToast.warning(
                                                    "Template will be updated",
                                                    "Saving will update the selected reusable template for future reports.",
                                                );
                                            }
                                        }}
                                    />
                                    <span className="rc-excel-check-box" aria-hidden="true" />
                                    <span>Update Selected Template</span>
                                </label>
                            </div>
                        ) : null}
                    </div>
                )}

                {showReportLayoutFields && (
                    <div className="rc-excel-control-row">
                        <label className="rc-label-block">
                            Sheet
                            <ReportConfigSelect<string>
                                value={addingSheet ? NEW_SHEET_OPTION : currentSheetName}
                                options={sheetOptions}
                                ariaLabel="Metric worksheet"
                                onChange={handleSheetChange}
                            />
                            <span className="rc-field-note">{sheetPosition ? `Position ${sheetPosition.position} of ${sheetPosition.total} on ${sheetPosition.sheetName}.` : "Choose the worksheet for this metric."}</span>
                        </label>
                        <label className="rc-label-block">
                            Block Title
                            <input
                                value={effectiveTitle}
                                onChange={(event) => {
                                    const title = event.target.value;
                                    updateLayout((current) => ({
                                        ...current,
                                        title,
                                        templateName: current.templateNameTouched ? current.templateName : templateNameForTitle(title),
                                    }));
                                }}
                            />
                        </label>

                        <label className="rc-label-block rc-excel-order">
                            Block Order
                            <input
                                type="number"
                                value={layout?.blockOrder ?? 10}
                                onChange={(event) => updateLayout((current) => ({ ...current, blockOrder: Number(event.target.value) || 0 }))}
                            />
                            <span className="rc-field-note">
                                {sheetPosition
                                    ? `Lower numbers appear first on ${currentSheetName}; this metric is position ${sheetPosition.position} of ${sheetPosition.total}.`
                                    : `Lower numbers appear first on ${currentSheetName}.`}
                            </span>
                        </label>
                    </div>
                )}
                {showReportLayoutFields && addingSheet && (
                    <div className="rc-excel-control-row rc-excel-control-row-single rc-excel-new-sheet-row">
                        <label className="rc-label-block">
                            New worksheet name
                            <input value={newSheetName} autoFocus onChange={(event) => setNewSheetName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") confirmNewSheet(); }} />
                            {(newSheetProblem || duplicateSheet || newSheetAssignmentProblem) && <span className="rc-field-note">{newSheetProblem || (duplicateSheet ? "That sheet name is already in use." : newSheetAssignmentProblem)}</span>}
                        </label>
                        <button className="rc-iconbtn success" type="button" title="Use worksheet" aria-label="Use worksheet" disabled={!!newSheetProblem || !!duplicateSheet || !!newSheetAssignmentProblem} onClick={confirmNewSheet}>
                            <span className="material-icons md-18" aria-hidden="true">check</span>
                        </button>
                        <button className="rc-iconbtn" type="button" title="Cancel new worksheet" aria-label="Cancel new worksheet" onClick={() => { setAddingSheet(false); setNewSheetName(""); }}>
                            <span className="material-icons md-18" aria-hidden="true">close</span>
                        </button>
                    </div>
                )}
            </div>

            {isDetailMode && (
                <div className="rc-excel-section rc-dcols" data-rc-validation={`excel-detail-columns-${section.id}`}>
                    <div className="rc-excel-section-head rc-dcols-head">
                        <div className="rc-dcols-head-text">
                            <h5>Sheet Columns</h5>
                            <p className="rc-hint">Order, header text and look of each column picked on the Metrics tab. Untick a column to leave it off the sheet.</p>
                        </div>
                        <div className="rc-dcols-summary" aria-live="polite">
                            <span className="rc-dcols-chip">{detailColumns.length} column{detailColumns.length === 1 ? "" : "s"}</span>
                            <span className="rc-dcols-chip">{onSheetCount} on sheet</span>
                            {aggregating && <span className="rc-dcols-chip accent">Grouped by {groupedCount} · {aggregatedCount} aggregated</span>}
                            {onSheetCount < detailColumns.length && (
                                <button type="button" className="rc-excel-action rc-dcols-showall" onClick={includeAllDetailColumns}>Show All On Sheet</button>
                            )}
                        </div>
                    </div>

                    <div className="rc-dcols-callout" role="note">
                        <span className="material-icons md-18" aria-hidden="true">functions</span>
                        <p>
                            <b>Aggregate</b> a column (Sum, Average, Minimum, Maximum, Count, Count Distinct) to get one row per group instead of one row per record: rows are grouped by every other column on the sheet. Columns off the sheet are left out of the query.
                            {aggregating && section.detailDistinct ? " \"Unique rows only\" on the Metrics tab has no effect while rows are grouped." : ""}
                        </p>
                    </div>

                    <ol className="rc-dcols-list">
                        {detailColumns.map((column, index) => {
                            const detail = column.detail;
                            const onSheet = detail?.include ?? true;
                            const aggregate = detailAggregateOf(column);
                            const valueType = detailOutputValueType(report, section, column.sourceAlias);
                            const allowed = allowedDetailAggregates(valueType);
                            const aggregateOptions = DETAIL_AGGREGATES
                                .filter((item) => allowed.includes(item.key) || item.key === aggregate)
                                .map((item) => ({ value: item.key, label: allowed.includes(item.key) ? item.label : `${item.label} (not valid here)` }));
                            const issue = aggregateIssues.find((item) => item.sourceAlias === column.sourceAlias);
                            const kind = detailColumnKind(column.sourceAlias, valueType);
                            const role = !onSheet
                                ? null
                                : aggregate !== "none"
                                    ? { text: detailAggregateDef(aggregate).label, tone: "aggregate" }
                                    : aggregating
                                        ? valueType === "constant" ? { text: "Same on every row", tone: "muted" } : { text: "Group by", tone: "group" }
                                        : null;
                            return (
                                <li className={["rc-dcol", onSheet ? "" : "off", issue ? "has-issue" : ""].filter(Boolean).join(" ")} key={column.sourceAlias}>
                                    <div className="rc-dcol-top">
                                        <div className="rc-dcol-order">
                                            <ExcelIconButton icon="keyboard_arrow_up" label={`Move ${column.label} up`} disabled={index === 0} onClick={() => moveDetailColumn(column.sourceAlias, -1)} />
                                            <ExcelIconButton icon="keyboard_arrow_down" label={`Move ${column.label} down`} disabled={index === detailColumns.length - 1} onClick={() => moveDetailColumn(column.sourceAlias, 1)} />
                                        </div>
                                        <span className="rc-dcol-index" aria-hidden="true">{index + 1}</span>
                                        <div className="rc-dcol-identity">
                                            <span className="rc-dcol-name" title={column.label}>{column.label}</span>
                                            <span className="rc-dcol-tags">
                                                <span className={`rc-dcol-tag ${kind.tone}`}>{kind.text}</span>
                                                {role && <span className={`rc-dcol-tag ${role.tone}`}>{role.text}</span>}
                                                {!onSheet && <span className="rc-dcol-tag muted">Not on sheet</span>}
                                            </span>
                                        </div>
                                        <label className="rc-dcol-switch" title={onSheet ? "Shown on the sheet" : "Left off the sheet"}>
                                            <input
                                                type="checkbox"
                                                role="switch"
                                                checked={onSheet}
                                                aria-label={`Show ${column.label} on the sheet`}
                                                onChange={(event) => updateDetailColumn(column.sourceAlias, (current) => ({ ...current, include: event.target.checked }))}
                                            />
                                            <span className="rc-dcol-switch-track" aria-hidden="true" />
                                            <span className="rc-dcol-switch-text">On sheet</span>
                                        </label>
                                    </div>

                                    {onSheet && (
                                        <div className="rc-dcol-fields">
                                            <label className="rc-dcol-field rc-dcol-field-header">
                                                <span className="rc-dcol-label">Header Text</span>
                                                <input
                                                    type="text"
                                                    value={detail?.header ?? column.label}
                                                    placeholder={column.label}
                                                    onChange={(event) => updateDetailColumn(column.sourceAlias, (current) => ({ ...current, header: event.target.value }))}
                                                />
                                            </label>
                                            <div className="rc-dcol-field">
                                                <span className="rc-dcol-label">Aggregate</span>
                                                <ReportConfigSelect<DetailAggregate>
                                                    value={aggregate}
                                                    options={aggregateOptions}
                                                    ariaLabel={`${column.label} aggregate`}
                                                    disabled={valueType === "constant"}
                                                    onChange={(next) => updateDetailColumn(column.sourceAlias, (current) => {
                                                        const previous = current.aggregate || "none";
                                                        // Keep a header the user typed; otherwise follow the aggregate ("Sum of Billed Amount").
                                                        const autoHeader = !current.header.trim() || current.header === column.label || current.header === detailAggregateHeader(previous, column.label);
                                                        return {
                                                            ...current,
                                                            aggregate: next === "none" ? undefined : next,
                                                            header: autoHeader ? detailAggregateHeader(next, column.label) : current.header,
                                                            format: next === previous ? current.format : defaultFormatForAggregate(next, valueType, current.format),
                                                        };
                                                    })}
                                                />
                                            </div>
                                            <div className="rc-dcol-field">
                                                <span className="rc-dcol-label">Format</span>
                                                <ReportConfigSelect<ExcelCellFormat>
                                                    value={detail?.format || "text"}
                                                    options={DETAIL_FORMAT_OPTIONS}
                                                    ariaLabel={`${column.label} format`}
                                                    onChange={(format) => updateDetailColumn(column.sourceAlias, (current) => ({ ...current, format }))}
                                                />
                                            </div>
                                            <div className="rc-dcol-field rc-dcol-field-width">
                                                <span className="rc-dcol-label">Width</span>
                                                <div className="rc-dcol-width">
                                                    <ReportConfigSelect<ExcelColumnWidthMode>
                                                        value={detail?.widthMode || "auto"}
                                                        options={DETAIL_WIDTH_MODE_OPTIONS}
                                                        ariaLabel={`${column.label} width mode`}
                                                        onChange={(widthMode) => updateDetailColumn(column.sourceAlias, (current) => ({ ...current, widthMode }))}
                                                    />
                                                    {detail?.widthMode === "fixed" && (
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={255}
                                                            aria-label={`${column.label} width in characters`}
                                                            title="Width in characters (1-255)"
                                                            value={detail?.width ?? 20}
                                                            onChange={(event) => updateDetailColumn(column.sourceAlias, (current) => ({ ...current, width: Number(event.target.value) || 0 }))}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <label className="rc-dcol-field rc-dcol-field-wrap">
                                                <span className="rc-dcol-label">Wrap Text</span>
                                                <span className="rc-dcol-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={detail?.wrapText ?? false}
                                                        onChange={(event) => updateDetailColumn(column.sourceAlias, (current) => ({ ...current, wrapText: event.target.checked }))}
                                                    />
                                                    <span>{detail?.wrapText ? "On" : "Off"}</span>
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                    {issue && (
                                        <p className="rc-dcol-issue" role="alert">
                                            <span className="material-icons md-16" aria-hidden="true">warning</span>
                                            <span>{issue.message}</span>
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                    {!detailColumns.length && <p className="rc-hint rc-dcols-empty">No visible DataModelColumn records are configured for this data model.</p>}

                    {aggregating && (
                        <div className="rc-dcols-totals" data-rc-validation={`excel-detail-totals-${section.id}`}>
                            <div className="rc-dcols-totals-row">
                                <label className="rc-dcol-switch">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={layout?.detailTotals?.show ?? false}
                                        onChange={(event) => updateLayout((current) => ({ ...current, detailTotals: { label: current.detailTotals?.label ?? "Total", show: event.target.checked } }))}
                                    />
                                    <span className="rc-dcol-switch-track" aria-hidden="true" />
                                    <span className="rc-dcol-switch-text">Grand total row under the data</span>
                                </label>
                                {layout?.detailTotals?.show && (
                                    <label className="rc-dcol-field rc-dcols-total-label">
                                        <span className="rc-dcol-label">Total Row Label</span>
                                        <input
                                            type="text"
                                            value={layout.detailTotals.label ?? ""}
                                            placeholder="Total"
                                            maxLength={100}
                                            onChange={(event) => updateLayout((current) => ({ ...current, detailTotals: { show: true, label: event.target.value } }))}
                                        />
                                    </label>
                                )}
                            </div>
                            <p className="rc-hint">Sum and Count are added up; Minimum / Maximum take the overall smallest / largest; Average is recalculated over all rows. Count Distinct has no total (a value can be in several groups). The label goes in the first column that is not aggregated.</p>
                        </div>
                    )}
                </div>
            )}

            {isCombinedMode && (
                <div className="rc-excel-section" data-rc-validation={`excel-combined-rows-${section.id}`}>
                    <div className="rc-excel-section-head">
                        <div>
                            <h5>Table Rows</h5>
                            <p className="rc-hint">Rows, their order and what they count are set on the Metrics tab. Here you choose each row's number format and whether it appears on the sheet. Totals are fixed: counts are summed and % rows are recalculated from the totals.</p>
                        </div>
                    </div>
                    <label className="rc-label-block rc-excel-combined-header">
                        Row Header Text
                        <input
                            value={combinedRowHeader}
                            onChange={(event) => {
                                const displayLabel = event.target.value;
                                updateColumn("breakdown", (current) => ({ ...current, rowLabel: { ...(current.rowLabel || defaultRowLabelOptions(current.label)), displayLabel } }));
                            }}
                        />
                    </label>
                    <div className="rc-excel-combined-list" role="table" aria-label="Table rows">
                        <div className="rc-excel-combined-row rc-excel-combined-head" role="row">
                            <span role="columnheader">Row</span>
                            <span role="columnheader">Shows</span>
                            <span role="columnheader">Format</span>
                            <span role="columnheader">Total</span>
                            <span role="columnheader">On Sheet</span>
                        </div>
                        {combinedRows(section).map((row) => {
                            const options = layout?.combinedRowOptions?.[row.id] || {};
                            const format = options.numberFormat || (row.value === "share" ? "percent0" : "accountingInt");
                            const setOptions = (change: Partial<typeof options>) => updateLayout((current) => ({
                                ...current,
                                combinedRowOptions: { ...(current.combinedRowOptions || {}), [row.id]: { ...(current.combinedRowOptions?.[row.id] || {}), ...change } },
                            }));
                            return (
                                <div className="rc-excel-combined-row" role="row" key={row.id}>
                                    <span role="cell" className="rc-excel-column-name">{combinedRowLabel(row)}</span>
                                    <span role="cell">{row.value === "share" ? "% of rule set" : "Count"}</span>
                                    <span role="cell">
                                        <ReportConfigSelect<ExcelNumberFormat>
                                            value={format}
                                            options={NUMBER_FORMAT_OPTIONS}
                                            ariaLabel={`${combinedRowLabel(row)} number format`}
                                            onChange={(numberFormat) => setOptions({ numberFormat })}
                                        />
                                    </span>
                                    <span role="cell"><span className="rc-badge">{row.value === "share" ? "Recalculated from totals" : "Sum of periods"}</span></span>
                                    <span role="cell">
                                        <label className="rc-checkbox-row rc-excel-toggle-card">
                                            <input type="checkbox" checked={!options.hidden} onChange={(event) => setOptions({ hidden: !event.target.checked })} />
                                            <span>Show</span>
                                        </label>
                                    </span>
                                </div>
                            );
                        })}
                        {!combinedRows(section).length && <p className="rc-hint">No rows yet. Add them on the Metrics tab.</p>}
                    </div>
                </div>
            )}

            {!isDetailMode && (
            <>
            <div className="rc-excel-section" data-rc-validation={`excel-columns-${section.id}`}>
                <div className="rc-excel-section-head">
                    <div>
                        <h5>Workbook Columns</h5>
                        <p className="rc-hint">{isTemplateLocked ? "This layout is loaded from a template. Check Save as Template to create or update a reusable layout." : "Assign each generated SQL column to a workbook role and adjust display options."}</p>
                    </div>
                </div>
                <div className="rc-excel-columns-list">
                    <div className="rc-excel-columns-head" aria-hidden="true">
                        <span>Database Column</span>
                        <span>Display As</span>
                        <span>Formatting Options</span>
                    </div>
                    {isCombinedMode && !workbookAliases.length && <p className="rc-hint">Add a date breakdown on the Metrics tab (Also Break Down By) to get one column per period.</p>}
                    {workbookAliases.map((item) => {
                        const column = layout?.columns.find((candidate) => candidate.sourceAlias === item.sourceAlias);
                        const role: ExcelColumnRole = column?.role || "ignore";
                        return (
                            <div className="rc-excel-column-row" key={item.sourceAlias}>
                                <div className="rc-excel-column-cell rc-excel-column-identity">
                                    <span className="rc-excel-cell-label">Database Column</span>
                                    <span className="rc-excel-column-name">{item.label}</span>
                                </div>
                                <div className="rc-excel-column-cell rc-excel-role-cell">
                                    <span className="rc-excel-cell-label">Display As</span>
                                    <ReportConfigSelect<ExcelColumnRole>
                                        value={role}
                                        options={ROLE_OPTIONS.filter((option) => (option.value !== "columnAxis" || item.extraBreakdownId) && (!isCombinedMode || option.value === "columnAxis" || option.value === "ignore"))}
                                        ariaLabel={`${item.label} workbook role`}
                                        disabled={isTemplateLocked}
                                        onChange={(nextRole) => updateColumn(item.sourceAlias, (current) => applyRoleDefaults(current, nextRole, section))}
                                    />
                                </div>
                                <div className="rc-excel-column-cell rc-excel-options-cell">
                                    <span className="rc-excel-cell-label">Formatting Options</span>
                                    {role === "rowLabel" && column?.rowLabel && (
                                        <div className="rc-excel-option-stack">
                                            <div className="rc-excel-option-controls rc-excel-rowlabel-controls">
                                                <label className="rc-label-block">
                                                    Header Text
                                                    <input
                                                        value={column.rowLabel.displayLabel}
                                                        disabled={isTemplateLocked}
                                                        onChange={(event) => updateColumn(item.sourceAlias, (current) => ({ ...current, rowLabel: { ...current.rowLabel!, displayLabel: event.target.value } }))}
                                                    />
                                                </label>
                                                {column.rowLabel.showTotalRow && (
                                                    <label className="rc-label-block">
                                                        Total Position
                                                        <ReportConfigSelect<ExcelTotalPosition>
                                                            value={column.rowLabel.totalPosition}
                                                            options={TOTAL_POSITION_OPTIONS}
                                                            ariaLabel={`${item.label} total position`}
                                                            disabled={isTemplateLocked}
                                                            onChange={(totalPosition) => updateColumn(item.sourceAlias, (current) => ({ ...current, rowLabel: { ...current.rowLabel!, totalPosition } }))}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                            <div className="rc-excel-option-toggles">
                                                <label className="rc-checkbox-row rc-excel-toggle-card">
                                                    <input
                                                        type="checkbox"
                                                        checked={column.rowLabel.showTotalRow}
                                                        disabled={isTemplateLocked}
                                                        onChange={(event) => updateColumn(item.sourceAlias, (current) => ({ ...current, rowLabel: { ...current.rowLabel!, showTotalRow: event.target.checked } }))}
                                                    />
                                                    <span>Show Total Rows</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    {role === "columnAxis" && column?.columnAxis && (
                                        <div className="rc-excel-option-stack">
                                            <div className="rc-excel-option-controls">
                                                <label className="rc-label-block">
                                                    Granularity
                                                    <span className="rc-config-select disabled">
                                                        <span className="rc-config-select-trigger" aria-label={`${item.label} granularity`}>
                                                            <span>Month</span>
                                                        </span>
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="rc-excel-option-toggles">
                                                <label className="rc-checkbox-row rc-excel-toggle-card">
                                                    <input
                                                        type="checkbox"
                                                        checked={column.columnAxis.fillMissingPeriods}
                                                        disabled={isTemplateLocked}
                                                        onChange={(event) => updateColumn(item.sourceAlias, (current) => ({ ...current, columnAxis: { ...current.columnAxis!, fillMissingPeriods: event.target.checked } }))}
                                                    />
                                                    <span>Fill missing values with 0</span>
                                                </label>
                                                <label className="rc-checkbox-row rc-excel-toggle-card">
                                                    <input
                                                        type="checkbox"
                                                        checked={column.columnAxis.showTotalColumn}
                                                        disabled={isTemplateLocked}
                                                        onChange={(event) => updateColumn(item.sourceAlias, (current) => ({ ...current, columnAxis: { ...current.columnAxis!, showTotalColumn: event.target.checked } }))}
                                                    />
                                                    <span>Show Total Columns</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    {role === "measure" && column?.measure && (
                                        <div className="rc-excel-option-stack">
                                            <div className="rc-excel-option-controls">
                                                <label className="rc-label-block">
                                                    Number Format
                                                    <ReportConfigSelect<ExcelNumberFormat>
                                                        value={column.measure.numberFormat}
                                                        options={NUMBER_FORMAT_OPTIONS}
                                                        ariaLabel={`${item.label} number format`}
                                                        disabled={isTemplateLocked}
                                                        onChange={(numberFormat) => updateColumn(item.sourceAlias, (current) => ({ ...current, measure: { ...current.measure!, numberFormat } }))}
                                                    />
                                                </label>
                                                {/*
                                                <label className="rc-label-block">
                                                    Scale
                                                    <ReportConfigSelect<ExcelMeasureScale>
                                                        value={column.measure.scale}
                                                        options={SCALE_OPTIONS}
                                                        ariaLabel={`${item.label} scale`}
                                                        onChange={(scale) => updateColumn(item.sourceAlias, (current) => ({ ...current, measure: { ...current.measure!, scale } }))}
                                                    />
                                                </label>
                                                */}
                                            </div>
                                        </div>
                                    )}
                                    {role === "ignore" && <span className="rc-excel-empty-options">This column will not appear in the workbook.</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/*
            {!hasColumnAxisSource && (
                <div className="rc-hint rc-excel-info-note">
                    <span className="material-icons md-18" aria-hidden="true">calendar_month</span>
                    <span>No month/quarter/year breakdown is configured yet - the sheet will only get one column of values.</span>
                    <button className="rc-excel-action" type="button" onClick={() => addTimeAxis("month")} title="Add month time axis">
                        <span className="material-icons md-16" aria-hidden="true">add</span>
                        <span>Add Month Axis</span>
                    </button>
                </div>
            )}
            */}

            <div className="rc-groupbox rc-excel-panel rc-excel-derived-panel" data-rc-validation={`excel-derived-${section.id}`}>
                <div className="rc-metric-top rc-excel-panel-head">
                    <span>
                        <b><span className="material-icons md-16" aria-hidden="true">functions</span> Derived Rows</b>
                        <small>Calculated workbook rows such as totals, rates, benchmarks, or remainders.</small>
                    </span>
                    <span className="rc-spacer" />
                    <button className="rc-excel-action" type="button" onClick={addDerivedRow} title="Add derived row" disabled={isTemplateLocked}>
                        <span className="material-icons md-16" aria-hidden="true">add</span>
                        <span>Add Derived Row</span>
                    </button>
                </div>
                {(layout?.derivedRows || []).map((row) => (
                    <div className="rc-inline-fields rc-excel-derived-row" key={row.id}>
                        <label className="rc-label-block">
                            Label
                            <input value={row.label} disabled={isTemplateLocked} onChange={(event) => updateDerivedRow(row.id, (current) => ({ ...current, label: event.target.value }))} />
                        </label>
                        <label className="rc-label-block">
                            Kind
                            <ReportConfigSelect<ExcelDerivedRowKind>
                                value={row.kind}
                                options={DERIVED_ROW_KIND_OPTIONS}
                                ariaLabel="Derived row kind"
                                disabled={isTemplateLocked}
                                onChange={(kind) => updateDerivedRow(row.id, (current) => ({
                                    ...current,
                                    kind,
                                    numberFormat: kind === "ratio" && current.numberFormat === DEFAULT_DERIVED_ROW_NUMBER_FORMAT ? "percent2" : current.numberFormat,
                                }))}
                            />
                        </label>
                        {row.kind === "constant" ? (
                            <label className="rc-label-block">
                                Value
                                <input type="number" value={row.constantValue ?? 0} disabled={isTemplateLocked} onChange={(event) => updateDerivedRow(row.id, (current) => ({ ...current, constantValue: Number(event.target.value) || 0 }))} />
                            </label>
                        ) : (
                            <label className="rc-label-block">
                                Source Rows
                                <input
                                    placeholder="Ex: Scheduled, Completed"
                                    value={row.sourceRowLabels.join(", ")}
                                    disabled={isTemplateLocked}
                                    onChange={(event) => updateDerivedRow(row.id, (current) => ({ ...current, sourceRowLabels: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) }))}
                                />
                            </label>
                        )}
                        <label className="rc-label-block">
                            Number Format
                            <ReportConfigSelect<ExcelNumberFormat>
                                value={row.numberFormat || DEFAULT_DERIVED_ROW_NUMBER_FORMAT}
                                options={NUMBER_FORMAT_OPTIONS}
                                ariaLabel={`${row.label || "Derived row"} number format`}
                                disabled={isTemplateLocked}
                                onChange={(numberFormat) => updateDerivedRow(row.id, (current) => ({ ...current, numberFormat }))}
                            />
                        </label>
                        <ExcelIconButton icon="delete_outline" label="Remove derived row" tone="danger" disabled={isTemplateLocked} onClick={() => removeDerivedRow(row.id)} />
                    </div>
                ))}
            </div>

            <div className="rc-groupbox rc-excel-panel rc-excel-chart-panel" data-rc-validation={`excel-chart-${section.id}`}>
                <div className="rc-excel-section-head">
                    <div>
                        <h5>Chart configuration</h5>
                        <p className="rc-hint">Choose whether this workbook block should include a chart.</p>
                    </div>
                </div>
                <div className="rc-excel-chart-grid">
                    <label className="rc-label-block">
                        {isCombo ? "Fallback chart type" : "Chart"}
                        <ReportConfigSelect<ExcelChartType>
                            value={layout?.chart.type || "none"}
                            options={CHART_TYPE_OPTIONS}
                            ariaLabel="Chart type"
                            disabled={isTemplateLocked}
                            onChange={(type) => updateLayout((current) => {
                                const isNewChart = current.chart.type === "none" && type !== "none";
                                const preservesLegacyOrientation = current.chart.type !== "none" && type === "none" && !current.chart.dataOrientation;
                                const dataOrientation = current.chart.dataOrientation
                                    || (preservesLegacyOrientation ? "rowsAsSeries" : undefined)
                                    || (isNewChart ? (current.columns.some((column) => column.role === "columnAxis") ? "rowsAsSeries" : "rowsAsCategories") : undefined);
                                return {
                                    ...current,
                                    chart: {
                                        ...current.chart,
                                        type,
                                        enabled: type !== "none",
                                        ...(dataOrientation ? { dataOrientation } : {}),
                                        ...(isNewChart && dataOrientation === "rowsAsCategories"
                                            ? { settings: { ...current.chart.settings!, showLegend: false } }
                                            : {}),
                                    },
                                };
                            })}
                        />
                        {isCombo && <small className="rc-field-note">Used when combo is turned off, or if none of the listed rows are in the data.</small>}
                    </label>
                    {layout?.chart.enabled && layout.chart.type !== "none" && (
                        <div className="rc-label-block rc-chart-mode-field">
                            <span>Series chart types</span>
                            <div className="rc-chart-mode-toggle" role="radiogroup" aria-label="Chart mode">
                                {CHART_MODE_OPTIONS.map((option) => (
                                    <label key={option.value} className={`rc-chart-mode-option ${(isCombo ? "combo" : "single") === option.value ? "selected" : ""}`}>
                                        <input
                                            type="radio"
                                            name={`chart-mode-${section.id}`}
                                            value={option.value}
                                            checked={(isCombo ? "combo" : "single") === option.value}
                                            disabled={isTemplateLocked}
                                            onChange={() => setChartMode(option.value)}
                                        />
                                        <span>
                                            <b>{option.label}</b>
                                            <small>{option.hint}</small>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {layout?.chart.enabled && layout.chart.type !== "none" && isCombo && (
                    <ComboChartEditor
                        chart={layout.chart}
                        suggestions={comboSuggestions}
                        disabled={isTemplateLocked}
                        onChange={(chart) => updateLayout((current) => ({ ...current, chart }))}
                    />
                )}
                {layout?.chart.enabled && layout.chart.type !== "none" && (
                    <ChartSettingsEditor
                        chart={layout.chart}
                        fallbackTitle={layout.title ?? section.name}
                        defaultDataOrientation={defaultChartDataOrientation}
                        disabled={isTemplateLocked}
                        onChange={(chart) => updateLayout((current) => ({ ...current, chart }))}
                    />
                )}
            </div>
            </>
            )}

            {problems.length > 0 && (
                <ul className="rc-hint rc-excel-layout-problems">
                    {problems.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
            )}

            <div className="rc-sql-preview-card rc-excel-preview-card">
                <div className="rc-sql-preview-head">
                    <span><span className="material-icons md-16" aria-hidden="true">grid_on</span> Layout Preview</span>
                    <span>Workbook preview</span>
                </div>
                <div className="rc-excel-preview-body">
                    <ExcelLayoutPreview
                        preview={preview}
                        chart={layout?.chart}
                        ratioRowLabels={(layout?.derivedRows || []).filter((row) => row.kind === "ratio" || /^percent/.test(row.numberFormat)).map((row) => row.label)}
                    />
                </div>
            </div>
        </section>
    );
}
